import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import './TeacherDashboard.css'
import '../styles/dashboard-header.css'
import LoadingScreen from '../components/LoadingScreen'

const TeacherDashboard = () => {
  const [user, setUser] = useState(null)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const history = useHistory()

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}')
    if (userData.role !== 'teacher') {
      // Redirect to appropriate dashboard
      if (userData.role === 'admin') {
        history.push('/admin-dashboard')
      } else if (userData.role === 'invigilator') {
        history.push('/invigilator-dashboard')
      } else if (userData.role === 'volunteer') {
        history.push('/volunteer-dashboard')
      } else {
        history.push('/student-dashboard')
      }
      return
    }

    setUser(userData)
    fetchDashboardData()
  }, [history])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/teacher/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDashboardData(data)
      } else {
        setError('Failed to load dashboard data')
      }
    } catch (err) {
      setError('Network error')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user')
    history.push('/login')
  }

  if (loading) {
    return <LoadingScreen message="Loading Teacher Dashboard" />
  }

  if (error) {
    return (
      <div className="teacher-dashboard-container">
        <div className="error">{error}</div>
      </div>
    )
  }

  return (
    <div className="teacher-dashboard-container">
      <header className="teacher-header">
        <div className="header-content">
          <h1>Teacher Dashboard</h1>
          <div className="user-info">
            <span>Welcome, {user?.full_name}</span>
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="teacher-main">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_tests || 0}</h3>
              <p>Total Tests</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.active_tests || 0}</h3>
              <p>Active Tests</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.total_attempts || 0}</h3>
              <p>Total Attempts</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>{dashboardData?.statistics?.average_score || 0}%</h3>
              <p>Average Score</p>
            </div>
          </div>
        </div>

        <div className="teacher-sections">
          <section className="recent-attempts">
            <h2>Recent Test Attempts</h2>
            <div className="td-attempts-list">
              {dashboardData?.recent_attempts?.map((attempt) => (
                <div key={attempt.id} className="td-attempt-item">
                  <div className="td-attempt-info">
                    <h4>{attempt.student_name}</h4>
                    <p>Test: {attempt.test_title}</p>
                    <p>Score: {attempt.percentage}%</p>
                    <p>Status: {attempt.status}</p>
                  </div>
                  <div className="td-attempt-date">
                    {new Date(attempt.created_at).toLocaleDateString()}
                  </div>
                </div>
              )) || <p>No recent attempts</p>}
            </div>
          </section>

          <section className="my-tests">
            <h2>My Tests</h2>
            <div className="td-tests-list">
              {dashboardData?.my_tests?.slice(0, 5).map((test) => (
                <div key={test.id} className="td-test-item">
                  <div className="td-test-info">
                    <h4>{test.title}</h4>
                    <p>Status: {test.status}</p>
                    <p>Attempts: {test.total_attempts || 0}</p>
                  </div>
                  <div className="td-test-actions">
                    <button 
                      className="td-view-button"
                      onClick={() => history.push(`/teacher/test/${test.id}`)}
                    >
                      View
                    </button>
                  </div>
                </div>
              )) || <p>No tests created yet</p>}
            </div>
          </section>
        </div>

        <div className="action-buttons">
          <button
            className="primary-button"
            onClick={() => history.push('/teacher/create-test')}
          >
            Create New Test
          </button>
          <button
            className="secondary-button"
            onClick={() => history.push('/teacher/tests')}
          >
            View All Tests
          </button>
          <button
            className="proctoring-button"
            onClick={() => history.push('/teacher/proctoring')}
          >
            Proctoring Dashboard
          </button>
        </div>
      </main>
    </div>
  )
}

export default TeacherDashboard
